import ModalViewModel from 'infra/ModalViewModel'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import JobStore, { FinalReturnYearInput } from 'jobs/JobStore'
import Job from 'jobs/Job'
import { AlertFactory } from 'alerts/Alert'
import { JobReturnAmountsYearDTO } from 'jobs/dtos/job'

let entryIdCounter = 0

/**
 * Converts the job's recorded amounts into dialog inputs. The server's own
 * `tax_year` keys are round-tripped rather than re-derived, which keeps a
 * Manual save aligned with the Extracted year it overrides.
 */
export function returnAmountsToYearInputs(
  amounts: JobReturnAmountsYearDTO[],
): FinalReturnYearInput[] {
  return amounts.map((year) => ({
    taxYear: year.tax_year,
    // `federal_amount` is absent when the year carries state amounts but no
    // federal amount. Round-trip that absence rather than defaulting it to
    // zero, or an untouched save would write a bogus $0 federal amount.
    federalAmount: year.federal_amount,
    stateAmounts: year.state_amounts.map((s) => ({
      state: s.state,
      amount: s.amount,
    })),
  }))
}

/**
 * A single per-state signed amount within a tax year block.
 */
export class StateAmountEntry {
  readonly id = ++entryIdCounter
  @observable state = ''
  @observable amount = 0

  constructor(state = '', amount = 0) {
    makeObservable(this)
    this.state = state
    this.amount = amount
  }

  @computed
  get isCompleted() {
    return this.state !== ''
  }
}

/**
 * A single tax year block: signed federal amount + per-state signed amounts.
 */
export class YearAmountEntry {
  readonly id = ++entryIdCounter
  @observable taxYear = ''
  @observable private federalAmountValue = 0
  /**
   * Whether this entry actually has a federal amount, distinct from the
   * displayed `federalAmount` value (which defaults to 0 for display even
   * when absent). Starts `false` when constructed without a federal amount
   * (a genuinely new entry, or an existing year with none) and flips to
   * `true` whenever `federalAmount` is assigned via its setter — whether by
   * the Pro editing the field or by other code. This keeps a year that is
   * saved untouched from writing a bogus $0 federal amount.
   */
  @observable hasFederalAmount = false
  @observable stateEntries: StateAmountEntry[] = []

  constructor(
    taxYear = '',
    federalAmount?: number,
    stateEntries: StateAmountEntry[] = [],
  ) {
    makeObservable(this)
    this.taxYear = taxYear
    this.federalAmountValue = federalAmount ?? 0
    this.hasFederalAmount = federalAmount !== undefined
    this.stateEntries = stateEntries
  }

  // Plain getter/setter, not `@computed` — the setter has a side effect
  // (flipping `hasFederalAmount`), which `@computed` accessors must not have.
  get federalAmount() {
    return this.federalAmountValue
  }

  set federalAmount(value: number) {
    this.federalAmountValue = value
    this.hasFederalAmount = true
  }

  @computed
  get hasIncompleteStates() {
    return this.stateEntries.some((e) => !e.isCompleted)
  }

  @computed
  get isCompleted() {
    return this.taxYear !== '' && !this.hasIncompleteStates
  }

  @action.bound
  addStateEntry() {
    this.stateEntries.push(new StateAmountEntry())
  }

  @action.bound
  removeStateEntry(index: number) {
    this.stateEntries.splice(index, 1)
  }
}

/**
 * Dialog for entering per-year final return amounts when a Pro marks a
 * RETAIL return as filed after uploading a Final Return document.
 *
 * Amounts are signed: positive = refund to client, negative = owed.
 */
export class FinalReturnAmountsDialogViewModel extends ModalViewModel {
  constructor(
    private jobStore: JobStore,
    private job: Job,
    private newAlert: AlertFactory,
    private onComplete: () => Promise<void>,
    private initialYears: () => FinalReturnYearInput[],
  ) {
    super()
    makeObservable(this)
    // Reset on close so a cancelled edit doesn't linger, and on show so a
    // reopened dialog re-reads the job's current amounts rather than the
    // stale snapshot taken at construction time (the two dialog owners are
    // built well before the dialog is ever shown).
    this.close$.subscribe(this.reset)
    this.show$.subscribe(this.reset)
    this.reset()
  }

  @observable
  yearEntries: YearAmountEntry[] = []

  @computed
  get hasIncompleteEntries() {
    return (
      this.yearEntries.length === 0 ||
      this.yearEntries.some((y) => !y.isCompleted)
    )
  }

  /**
   * Default tax year for a newly added entry, mirroring the backend's
   * `TaxYear.Default` (max(job.years) - 1). Only applies to genuinely new
   * entries — prefill for existing years comes from `initialYears` via
   * `reset`, not from this default.
   */
  private get defaultTaxYear() {
    return String(new Date().getFullYear() - 1)
  }

  /**
   * Selectable tax years for the dropdown: the current calendar year down
   * through the previous ten (late filings and amendments reach back years).
   */
  get taxYearOptions(): [string, string][] {
    const current = new Date().getFullYear()
    return Array.from({ length: 11 }, (_, i) => {
      const year = String(current - i)
      return [year, year]
    })
  }

  @action.bound
  addYearEntry() {
    this.yearEntries.push(new YearAmountEntry(this.defaultTaxYear))
  }

  @action.bound
  removeYearEntry(index: number) {
    this.yearEntries.splice(index, 1)
  }

  /**
   * Rebuilds the entries from the job's recorded amounts so the next open
   * starts from the current server state, discarding any uncommitted edits.
   */
  @action.bound
  reset() {
    const initial = this.initialYears()

    this.yearEntries = initial.length
      ? initial.map(
          (year) =>
            new YearAmountEntry(
              year.taxYear,
              year.federalAmount,
              year.stateAmounts.map(
                (s) => new StateAmountEntry(s.state, s.amount),
              ),
            ),
        )
      : [new YearAmountEntry(this.defaultTaxYear)]
  }

  submit = task.resolved(async () => {
    if (this.hasIncompleteEntries) {
      return
    }

    const years: FinalReturnYearInput[] = this.yearEntries.map((y) => ({
      taxYear: y.taxYear,
      federalAmount: y.hasFederalAmount ? y.federalAmount : undefined,
      stateAmounts: y.stateEntries.map((s) => ({
        state: s.state,
        amount: s.amount,
      })),
    }))

    try {
      await this.jobStore.recordJobReturnAmounts(this.job.id, years)
      await this.onComplete()
      this.close()
    } catch (err) {
      this.newAlert().fromError(err).show()
    }
  })
}
